import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { userRoles } from '../../../../environments/environment';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddEventTypeComponent } from '../../../shared/components/dialog/add-event-type/add-event-type.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../../shared/components/dialog/commom/commom-dialog.component';
import { FilterMenuComponent } from '../../../shared/components/filter/filter-menu.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

interface EventType {
  id: string;
  eventTypeName: string;
  pointsAwarded: number;
  eventTypeId: string;
  status: string;
  pointsMethodName: string;
  eventTypeImageDescription: string;
  receivedAttendingPoints: number;
  engagementCount: number;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: EventType | [];
}

interface FilterData {
  [key: string]: any;
}

@Component({
  selector: 'app-event-type',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ButtonComponent,
    MatDialogModule,
    AddEventTypeComponent,
    FormsModule,
  ],
  templateUrl: './event-type.component.html',
  styleUrls: ['./event-type.component.scss'],
})
export class EventTypeComponent implements OnInit, OnDestroy {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('addEventTypeContent') addEventTypeContent!: TemplateRef<any>;
  @ViewChild('filterMenu') filterMenu!: FilterMenuComponent;
  @ViewChild('filterDropdownContainer') filterDropdownContainer!: ElementRef;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  loading = false;
  submitLoading: Boolean = false;
  data: EventType[] = [];
  editingEventType: EventType | null = null;
  columns: string[] = [
    's.No',
    'event type',
    'points method',
    'PD points',
    'engagement count',
    'status',
  ];
  fullData: any[] = [];
  search: string = '';
  selectedStatus: string = 'all';
  showFilterMenu = false;
  currentFilters: FilterData = {};
  filterBackdrop = false;
  userRoles = {
    addNew: false,
    genarateReport: false,
    listItem: {
      edit: false,
      statusChange: false,
    },
  };

  showStatusDropdown = false;
  statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
  ];

  private statusDropdownClickListener: (() => void) | null = null;
  private documentClickListener: (() => void) | null = null;

  constructor(
    private dialog: MatDialog,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.fetchEventTypes();

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.eventType;
    }
  }

  ngOnDestroy(): void {
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
    }
  }

  fetchEventTypes(): void {
    this.loading = true;

    this.httpService.get<APIResponse>('eventTypes?status=2').subscribe({
      next: (response) => {
        const dataArray = Array.isArray(response.data)
          ? response.data
          : response.data
            ? [response.data]
            : [];
        this.fullData = dataArray.map((item: any, index: number) => ({
          id: item.eventTypeId,
          'event type': item.eventTypeName || '-',
          eventTypeName: item.eventTypeName || '-',
          'PD points': item.receivedAttendingPoints || '0',
          'engagement count': item?.engagementCount
            ? item?.engagementCount
            : '0',
          status: item.status ?? 'Active',
          's.No': index + 1,
          'points method': item?.pointsMethodName,
          eventType: item.eventTypeName || '-',
          pdPoints: item.receivedAttendingPoints || '0',
          engagementCount: item?.engagementCount ? item?.engagementCount : '0',
          pointsMethod: item?.pointsMethodName,
          isStatusClickable: true,
          eventTypeImageDescription: item?.eventTypeImageDescription,
          eventTypeImage: item?.eventTypeImage,
          imageId: item?.imageId,
        }));

        this.applyFilter();
      },
      error: (err) => {
        console.error('Failed to load Event Types:', err);
        this.showErrorToast(err?.message);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  toggleFilterMenu(event: Event): void {
    event.stopPropagation();
    this.showFilterMenu = !this.showFilterMenu;
    if (this.showFilterMenu) {
      if (this.documentClickListener) {
        document.removeEventListener('click', this.documentClickListener);
        this.documentClickListener = null;
      }
      if (this.filterMenu) {
        this.filterMenu.filterForm.patchValue(this.currentFilters);
      }
    } else {
      this.closeFilterMenu();
    }
  }

  closeFilterMenu(): void {
    this.showFilterMenu = false;

    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = null;
    }
  }

  onFiltersApplied(filters: FilterData): void {
    this.currentFilters = { ...filters };
    this.applyFilter();
    this.closeFilterMenu();
    this.showSuccessToast('Filters applied successfully');
  }

  onFiltersCleared(): void {
    this.currentFilters = {};
    this.applyFilter();
    this.closeFilterMenu();
    this.showSuccessToast('Filters cleared');
  }

  updateSearch(searchValue: string): void {
    this.search = searchValue;
    this.applyFilter();
  }

  // applyFilter(): void {
  //     if (!this.search || this.search.trim() === '') {
  //         this.data = [...this.fullData];
  //         this.cdr.detectChanges();
  //         return;
  //     }
  //     const searchTerm = this.search.trim().toLowerCase();
  //     this.data = this.fullData.filter(item => {
  //         const regionNameMatch = item.eventType?.toLowerCase().includes(searchTerm) || false;
  //         // const pdPointsMatch = item.pdPoints?.toString().toLowerCase().includes(searchTerm) || false;
  //         // const engagementCountMatch = item.engagementCount?.toString().toLowerCase().includes(searchTerm) || false;
  //         // const pointsMethodMatch = item.pointsMethod?.toLowerCase().includes(searchTerm) || false;
  //         const statusMatch = item.status?.toLowerCase().startsWith(searchTerm) || false;

  //         return (
  //             regionNameMatch ||
  //             // pdPointsMatch ||
  //             // engagementCountMatch ||
  //             // pointsMethodMatch ||
  //             statusMatch
  //         );
  //     });

  //     this.cdr.detectChanges();
  // }

  getStatusClass(status: any): string {
    switch (status) {
      case 'all':
        return 'All';
      case 'active':
        return 'Active';
      default:
        return 'Inactive';
    }
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.showStatusDropdown = !this.showStatusDropdown;

    if (this.showStatusDropdown) {
      this.addStatusDropdownClickListener();
    } else {
      this.removeStatusDropdownClickListener();
    }
    this.cdr.detectChanges();
  }

  closeStatusDropdown(): void {
    this.showStatusDropdown = false;
    this.removeStatusDropdownClickListener();
    this.cdr.detectChanges();
  }

  private addStatusDropdownClickListener(): void {
    this.removeStatusDropdownClickListener();
    this.statusDropdownClickListener = () => {
      this.closeStatusDropdown();
    };
    setTimeout(() => {
      document.addEventListener('click', this.statusDropdownClickListener!);
    }, 0);
  }

  private removeStatusDropdownClickListener(): void {
    if (this.statusDropdownClickListener) {
      document.removeEventListener('click', this.statusDropdownClickListener);
      this.statusDropdownClickListener = null;
    }
  }

  updateEventStatus(newStatus: any): void {
    this.selectedStatus = newStatus.value || newStatus;
    this.applyFilter();
    this.closeStatusDropdown();
  }

  applyFilter(): void {
    let filteredData = [...this.fullData];
    if (this.selectedStatus && this.selectedStatus !== 'all') {
      filteredData = filteredData.filter(
        (item) =>
          item.status?.toLowerCase() === this.selectedStatus.toLowerCase(),
      );
    }

    if (this.search && this.search.trim() !== '') {
      // Saved names can carry doubled or non-breaking spaces, which HTML renders identically
      // to a single space - collapse runs of whitespace on both sides before comparing.
      const normalize = (value: unknown): string =>
        (value ?? '')
          .toString()
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();

      const searchTerm = normalize(this.search);
      filteredData = filteredData.filter((item) => {
        const searchableFields = [
          item['s.No'],
          item['event type'],
          item['points method'],
          item['PD points'],
          item['engagement count'],
          item['status'],
        ];
        return searchableFields.some((value) =>
          normalize(value).includes(searchTerm),
        );
      });
    }

    this.data = filteredData;
    this.resetPagination();
    this.cdr.detectChanges();
  }

  private resetPagination(): void {
    this.data = [...this.data];
    if (this.tableComponent) {
      this.tableComponent.currentPage = 1;
      this.tableComponent.cdr?.detectChanges();
    }
  }

  onEventTypeFormSubmit(formData: any): void {
    if (this.submitLoading) return;
    this.submitLoading = true;
    const dialogRefs = this.dialog.openDialogs;
    const currentDialogRef = dialogRefs[dialogRefs.length - 1];

    if (
      currentDialogRef &&
      currentDialogRef.componentInstance instanceof CommonDialogComponent
    ) {
      if (this.editingEventType) {
        currentDialogRef.componentInstance.data.confirmText = 'Updating...';
      } else {
        currentDialogRef.componentInstance.data.confirmText = 'Submitting...';
      }
      currentDialogRef.componentInstance.data.loading = true;
      currentDialogRef.componentInstance.cdr.detectChanges();
    }
    const payload = {
      eventTypeName: formData.eventTypeName,
      receivedAttendingPoints: formData.pointsAwarded,
      pointsMethodId: formData?.pointsMethod,
      ...(formData?.imageId && { imageId: formData.imageId }),
      eventTypeImageDescription: formData?.eventTypeImageDescription,
    };

    const apiCall = this.editingEventType
      ? this.httpService.put<APIResponse>(
          `eventtypes/${this.editingEventType.id}`,
          payload,
        )
      : this.httpService.post<APIResponse>('eventtypes', payload);

    apiCall.subscribe({
      next: (response) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.dialog.closeAll();
          this.fetchEventTypes();
          this.editingEventType = null;
        } else {
          this.showErrorToast(response.message);
          if (
            currentDialogRef &&
            currentDialogRef.componentInstance instanceof CommonDialogComponent
          ) {
            currentDialogRef.componentInstance.data.confirmText = this
              .editingEventType
              ? 'Update'
              : 'Submit';
            currentDialogRef.componentInstance.data.loading = false;
            currentDialogRef.componentInstance.cdr.detectChanges();
          }
          this.submitLoading = false;
          return;
        }
      },
      error: (err) => {
        if (err) {
          this.showErrorToast(err?.message);
        } else {
          this.showErrorToast(
            err?.message || 'Something went wrong. Please try again later.',
          );
        }

        if (
          currentDialogRef &&
          currentDialogRef.componentInstance instanceof CommonDialogComponent
        ) {
          currentDialogRef.componentInstance.data.confirmText = this
            .editingEventType
            ? 'Update'
            : 'Submit';
          currentDialogRef.componentInstance.data.loading = false;
          currentDialogRef.componentInstance.cdr.detectChanges();
        }
        this.submitLoading = false;
      },
      complete: () => {
        this.submitLoading = false;
      },
    });
  }

  openAddDialog(eventTypeToEdit: EventType | null = null): void {
    this.editingEventType = eventTypeToEdit;

    const dialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: eventTypeToEdit ? 'Edit event type' : 'Add new event type',
        confirmText: eventTypeToEdit ? 'Update' : 'Submit',
        cancelText: 'Cancel',
        template: this.addEventTypeContent,
        loading: false,
      },
      width: '50.5rem',
      maxWidth: '100vw',
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.editingEventType = null;
    });
  }

  onEdit(eventType: EventType): void {
    this.openAddDialog(eventType);
  }

  onDelete(eventType: EventType): void {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: {
        title: 'Confirm delete',
        message: `Are you sure you want to delete ${eventType.eventTypeName}?`,
        okText: 'Delete',
        cancelText: 'Cancel',
        icon: 'warning',
        submessage: '',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteEventType(eventType);
      }
    });
  }

  navigateReports(): void {
    this.router.navigate(['/reports/report-dashboard'], {
      state: { activeTab: 'Event type report' },
    });
  }

  onStatusClick(event: { row: any; id: any }): void {
    if (!this.userRoles.listItem.statusChange) return;
    const { row, id } = event;
    const currentStatus = row.status;
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

    const dialogData: AlertDialogData = {
      title: 'Status update',
      message: `Are you sure you want to ${newStatus === 'Active' ? 'Activate' : 'Deactivate'}?`,
      okText: 'Update',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const apiStatus = newStatus === 'Active';

        this.httpService
          .put<APIResponse>(`eventtypes/${id}/status`, { status: apiStatus })
          .subscribe({
            next: (response) => {
              if (response?.status) {
                this.updateLocalRegionStatus(id, newStatus);
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: () => {
              this.showErrorToast(
                'An error occurred while updating the region status.',
              );
            },
          });
      }
    });
  }

  goBack() {
    this.location.back();
  }

  private deleteEventType(eventType: EventType): void {
    this.httpService
      .delete<APIResponse>(`eventTypes/${eventType.id}`)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.fullData = this.fullData.filter(
              (item) => item.id !== eventType.id,
            );
            this.data = this.data.filter((item) => item.id !== eventType.id);
            this.showSuccessToast(response.message);
          } else {
            this.showErrorToast(response.message);
            return;
          }
        },
        error: (err) => {
          console.error('Failed to delete Event Type:', err);
          this.showErrorToast(err?.message);
        },
      });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  private updateLocalRegionStatus(regionId: string, newStatus: string): void {
    this.fullData = this.fullData.map((item) => {
      if (item.id === regionId) {
        return {
          ...item,
          status: newStatus,
        };
      }
      return item;
    });

    this.data = this.data.map((item) => {
      if (item.id === regionId) {
        return {
          ...item,
          status: newStatus,
        };
      }
      return item;
    });

    this.cdr.detectChanges();
  }
}
